import axios, { AxiosError, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import i18n from '@/i18n/config';
import { useAuthStore } from '@/stores';
import { PROTECTED_ROUTES, PAYWALL_TOAST_STORAGE_KEY } from '@/constants/routes';
import type { ApiErrorResponse } from '@/types';
import { tokenRefreshService } from './tokenRefresh';

// Helper function to navigate to login page
const navigateToLogin = () => {
  console.log('Navigating to login page...');
  // Use replace to avoid leaving the current page in browser history
  window.location.replace('/login');
};

// Runtime environment configuration interface
interface RuntimeEnv {
  API_BASE_URL?: string;
  APP_NAME?: string;
  APP_VERSION?: string;
}

// Extend Window interface
declare global {
  interface Window {
    _env_?: RuntimeEnv;
  }
}

// Get API Base URL (supports runtime configuration)
const getApiBaseUrl = (): string => {
  // Priority:
  // 1. Runtime configuration (from Task Definition environment variables)
  // 2. Build-time configuration (from .env file VITE_API_BASE_URL)
  // 3. Default value (development environment)

  const runtimeConfig = window._env_?.API_BASE_URL;
  const buildTimeConfig = import.meta.env.VITE_API_BASE_URL;
  const defaultUrl = 'http://localhost:5051';

  const apiUrl = runtimeConfig || buildTimeConfig || defaultUrl;

  return apiUrl;
};

// Create API client instance
const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
  withCredentials: true, // Enable Cookie cross-domain transmission
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add authentication token
const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  const { accessToken, isAuthenticated } = useAuthStore.getState();

  if (isAuthenticated() && accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
};

// Response success handler - Check for auto-refreshed token
const responseSuccessHandler = (response: AxiosResponse) => {
  const { headers } = response;

  // Check if there is a new access token (backend auto-refresh mechanism)
  const newAccessToken = headers['x-new-access-token'];
  if (newAccessToken) {
    const { setTokenAuth, refreshToken } = useAuthStore.getState();
    setTokenAuth({ access_token: newAccessToken, refresh_token: refreshToken || '' });
    console.log('Token automatically refreshed by backend');
  }

  return response;
};

// Note: Token refresh logic is now centralized in tokenRefreshService
// This allows sharing the refresh lock between Axios and native fetch (authFetch)

// Response interceptor - Unified error handling + automatic token refresh
const responseErrorHandler = async (error: AxiosError) => {
  const { clearAuth, isAuthenticated } = useAuthStore.getState();
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  console.log('API Error:', error);
  console.log('Error Response:', error.response);
  console.log('Error Request:', error.request);

  if (error.response) {
    const { status, data } = error.response;

    // Handle 401 error for guest users - silent fail
    if (status === 401 && !isAuthenticated()) {
      console.log('[Guest] API requires authentication');
      return Promise.reject(error);
    }

    // Handle 401 error - Attempt to refresh token using centralized service
    if (status === 401 && !originalRequest._retry && isAuthenticated()) {
      originalRequest._retry = true;

      try {
        // tokenRefreshService handles the refresh lock and subscriber queue internally
        const newToken = await tokenRefreshService.refreshToken();

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - tokenRefreshService already cleared auth
        toast.error(i18n.t('common:auth.sessionExpired'));
        navigateToLogin();
        return Promise.reject(refreshError);
      }
    }

    // Extract error message from unified error format: { error: { message: string } }
    const getErrorMessage = (): string => {
      if (data && typeof data === 'object' && 'error' in data) {
        const errorData = data as ApiErrorResponse;
        return errorData.error.message;
      }
      return '';
    };

    const errorMessage = getErrorMessage();

    // Show error message based on status code
    switch (status) {
      case 400:
        toast.error(errorMessage || 'Invalid request parameters');
        break;
      case 401:
        toast.error(errorMessage || 'Login status has expired, please log in again');
        clearAuth();
        navigateToLogin();
        break;
      case 402: {
        // Quota exceeded / paid-only feature — redirect to subscription page.
        // Exempt the /subscription/me status probe: it is the diagnostic endpoint
        // the success page polls, and must observe 402 without global side effects.
        const isMeProbe = (originalRequest?.url ?? '').includes('/subscription/me');
        if (!isMeProbe) {
          const message = errorMessage || i18n.t('subscription:paywall.trialExpired');
          // Guard against redirect loop when already on a /subscription route
          // (exact prefix to avoid colliding with future /subscriptions-* routes).
          const path = window.location.pathname;
          const onSubscription =
            path === PROTECTED_ROUTES.SUBSCRIPTION ||
            path.startsWith(`${PROTECTED_ROUTES.SUBSCRIPTION}/`);
          if (onSubscription) {
            // Already on the subscription page — show the toast immediately.
            toast.error(message);
          } else {
            // Stash the message and hard-navigate; ManageSubscriptionPage shows it
            // on mount, so the full-page reload doesn't swallow the toast.
            sessionStorage.setItem(PAYWALL_TOAST_STORAGE_KEY, message);
            window.location.assign(PROTECTED_ROUTES.SUBSCRIPTION);
          }
        }
        break;
      }
      case 403:
        toast.error(errorMessage || 'Insufficient permissions to perform this action');
        break;
      case 404:
        toast.error(errorMessage || 'The requested resource does not exist');
        break;
      case 409:
        toast.error(errorMessage || 'Resource conflict');
        break;
      case 422:
        toast.error(errorMessage || 'Data validation failed');
        break;
      case 429:
        toast.error(errorMessage || 'Too many requests, please try again later');
        break;
      case 500:
        toast.error(errorMessage || 'Internal server error, please try again later');
        break;
      default:
        toast.error(errorMessage || `Error ${status}: Request failed`);
    }
  } else if (error.request) {
    // Check if it may be an authentication issue (network error caused by token expiration)
    if (isAuthenticated()) {
      toast.error('Login status may have expired, please log in again');
      clearAuth();
      navigateToLogin();
    } else {
      toast.error('Network connection failed, please check your network status');
    }
  } else {
    toast.error('An unknown error occurred, please try again later');
  }

  return Promise.reject(error);
};

// Set up interceptors
apiClient.interceptors.request.use(requestInterceptor, (error: AxiosError) =>
  Promise.reject(error)
);
apiClient.interceptors.response.use(
  responseSuccessHandler,
  responseErrorHandler as (error: AxiosError) => Promise<never>
);

export default apiClient;
